import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';

import { useAlertDetail } from '@/api/hooks/useAlertDetail';
import { useAlertTransactions } from '@/api/hooks/useAlertTransactions';
import { useAlertBaseline } from '@/api/hooks/useAlertBaseline';
import { useAlertRelated } from '@/api/hooks/useAlertRelated';
import { useAlertRuleAudit } from '@/api/hooks/useAlertRuleAudit';

import { HeaderSection } from '@/components/alert-detail/HeaderSection';
import { LLMInvestigationSection } from '@/components/alert-detail/LLMInvestigationSection';
import { WhyFiredSection } from '@/components/alert-detail/WhyFiredSection';
import { BaselineComparisonSection } from '@/components/alert-detail/BaselineComparisonSection';
import { TransactionMapSection } from '@/components/alert-detail/TransactionMapSection';
import { RelatedAlertsSection } from '@/components/alert-detail/RelatedAlertsSection';
import { RuleAuditSection } from '@/components/alert-detail/RuleAuditSection';
import { DispositionSection } from '@/components/alert-detail/DispositionSection';

/**
 * /app/fraud/{alertId} — alert detail page (PR 2 redesign).
 *
 * Top-level orchestrator only: it wires all five hooks and stacks the
 * eight section components in narrative scroll order. Each section
 * receives its own query result so the page renders progressively as
 * data lands (we don't block the whole page on the slowest endpoint).
 *
 * Section order (per architect blueprint + user PR 2 overrides):
 *   1. HeaderSection
 *   2. LLMInvestigationSection   ← moved up from middle (user feedback)
 *   3. WhyFiredSection
 *   4. BaselineComparisonSection
 *   5. TransactionMapSection     ← placeholder in PR 2; map embed in PR 4
 *   6. RelatedAlertsSection
 *   7. RuleAuditSection          ← collapsed accordion, default closed
 *   8. DispositionSection
 *
 * Per-rule custom evidence renderers land in PR 3.
 */
export default function AlertDetail(): JSX.Element {
  const { alertId: alertIdParam } = useParams<{ alertId: string }>();
  const alertId = alertIdParam ? Number.parseInt(alertIdParam, 10) : undefined;

  const detail = useAlertDetail(alertId);
  const transactions = useAlertTransactions(alertId);
  const baseline = useAlertBaseline(alertId);
  const related = useAlertRelated(alertId);
  const ruleAudit = useAlertRuleAudit(alertId);

  const backLink = (
    <Button variant="ghost" size="sm" asChild className="w-fit">
      <Link to="/fraud">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Fraud Center
      </Link>
    </Button>
  );

  // Block only on the *header* query — every other section has its own
  // skeleton so they can race independently.
  if (detail.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="flex flex-col gap-2">
        {backLink}
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Failed to load alert</CardTitle>
            <CardDescription>
              {detail.error?.message ?? 'Unknown error'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const alert = detail.data;

  return (
    <div className="flex flex-col gap-4">
      {backLink}

      {/* 1. Header */}
      <HeaderSection alert={alert} />

      <Separator className="my-2" />

      {/* 2. LLM investigation (moved up: investigator fires LLM early
             and reads narrative as the lead-in to evidence sections) */}
      <LLMInvestigationSection alertId={alertId} />

      {/* 3. Why this rule fired */}
      <div className="mt-8">
        <WhyFiredSection alert={alert} />
      </div>

      {/* 4. Baseline comparison */}
      <div className="mt-8">
        <BaselineComparisonSection query={baseline} />
      </div>

      {/* 5. Transaction map (placeholder in PR 2) */}
      <div className="mt-8">
        <TransactionMapSection query={transactions} />
      </div>

      {/* 6. Related alerts */}
      <div className="mt-8">
        <RelatedAlertsSection query={related} />
      </div>

      {/* 7. Rule audit (collapsed accordion; hidden when rows_available=false) */}
      <div className="mt-8">
        <RuleAuditSection query={ruleAudit} />
      </div>

      {/* 8. Disposition */}
      <div className="mt-8">
        <DispositionSection alert={alert} />
      </div>
    </div>
  );
}
